import { Component, OnDestroy, OnInit, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subscription } from 'rxjs';

import { AufnahmePunktDocument, istPunktartMitProtokoll } from '../../data-model';
import { Command, NavbarService } from 'src/app/navbar.service';
import { AuthService } from 'src/app/auth.service';
import { DataService } from 'src/app/data.service';
import { FileExportService, SelectionService, ViewportService, ConfirmService } from '@luzula/luzula-lib';
import { ProtokollPdfService } from './protokoll-pdf.service';

@Component({
    standalone: false,
	selector: 'bp-multi-print-view',
	templateUrl: './multi-print-view.component.html',
	styleUrls: ['./multi-print-view.component.css'],
	
})
export class MultiPrintViewComponent implements OnInit, OnDestroy {
	@ViewChild('printContent') printContent!: ElementRef;

	bps: AufnahmePunktDocument[] = [];
	loading = true;
	pdfExporting = false;
	pdfPreviewUrl: SafeResourceUrl | null = null;
	private pdfBlobUrl: string | null = null;

	istPunktartMitProtokoll = istPunktartMitProtokoll;

	private queryParamsSubscription?: Subscription;
	private authChangedSubscription?: Subscription;
	constructor(
		public ds: DataService,
		public vps: ViewportService,
		private aus: AuthService,
		private ns: NavbarService,
		private route: ActivatedRoute,
		private router: Router,
		private selectionService: SelectionService,
		private fileExport: FileExportService,
		private pdfService: ProtokollPdfService,
		private sanitizer: DomSanitizer,
		private confirmService: ConfirmService,
	) { }

	private initButtons() {
		this.ns.subscribe(Command.goBackToPoint, () => this.onClose());
		this.ns.subscribe(Command.print, () => this.onPrint());
	}

	async ngOnInit() {
		this.queryParamsSubscription = this.route.queryParams.subscribe(async params => {
			const idsParam = params['ids'];
			if (idsParam) {
				const ids = idsParam.split(',');
				await this.loadPoints(ids);
			}
		});
		this.authChangedSubscription = this.aus.authChanged.subscribe(() => this.initButtons());
		this.initButtons();
	}

	private async loadPoints(ids: string[]) {
		this.loading = true;
		this.bps = [];

		try {
			// Alle Punkte in einem Batch laden (eine Anfrage statt vieler)
			// KEIN WMS-Fetch für Massenansicht — WMS-Daten werden beim Einzelaufruf gespeichert (Auto-Save)
			const docs = await this.ds.getBohrpunkteBatch(ids);
			this.bps = docs.map(bp => this.ds.updateBohrpunkt(bp, false));
		} catch (e) {
			console.error('Failed to load points:', e);
		}

		this.loading = false;

		// PDF-Vorschau generieren (nur auf Desktop, nicht auf mobilen Geräten)
		if (this.bps.length > 0 && !this.vps.isMobile) {
			await this.generatePdfPreview();
		}
	}

	private async generatePdfPreview() {
		try {
			const pdfBlob = await this.pdfService.generatePdf(this.bps);
			// Alte URL freigeben
			if (this.pdfBlobUrl) {
				URL.revokeObjectURL(this.pdfBlobUrl);
			}
			this.pdfBlobUrl = URL.createObjectURL(pdfBlob);
			this.pdfPreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.pdfBlobUrl);
		} catch (e) {
			console.error('PDF preview generation failed:', e);
		}
	}

	ngOnDestroy(): void {
		this.ns.unsubscribe(Command.goBackToPoint);
		this.ns.unsubscribe(Command.print);
		this.queryParamsSubscription?.unsubscribe();
		this.authChangedSubscription?.unsubscribe();
		// Blob-URL freigeben
		if (this.pdfBlobUrl) {
			URL.revokeObjectURL(this.pdfBlobUrl);
		}
	}

	onClose() {
		this.router.navigate(['/']);
	}

	onPrint() {
		window.print();
	}

	async onSavePdf() {
		console.log('[PDF Export] Starting, bps:', this.bps.length, 'exporting:', this.pdfExporting);
		if (this.bps.length === 0 || this.pdfExporting) return;

		const filename = this.bps.length === 1
			? `${this.bps[0].header.nummer || 'Punkt'}.pdf`
			: `Bohrpunkte_${this.bps.length}.pdf`;
		console.log('[PDF Export] Filename:', filename);

		// Speicherort ZUERST abfragen (muss direkt nach User-Click passieren)
		let fileHandle: any = null;
		if ('showSaveFilePicker' in window) {
			try {
				console.log('[PDF Export] Opening save picker...');
				fileHandle = await (window as any).showSaveFilePicker({
					suggestedName: filename,
					types: [{
						description: 'PDF-Dokument',
						accept: { 'application/pdf': ['.pdf'] }
					}]
				});
				console.log('[PDF Export] File handle obtained');
			} catch (e: any) {
				if (e.name === 'AbortError') {
					console.log('[PDF Export] User cancelled');
					return; // User hat abgebrochen
				}
				console.warn('Save picker nicht verfügbar:', e);
			}
		}

		this.pdfExporting = true;
		console.log('[PDF Export] Generating PDF...');

		try {
			// PDF mit pdfmake generieren
			const pdfBlob = await this.pdfService.generatePdf(this.bps);
			console.log('[PDF Export] PDF generated, size:', pdfBlob.size);

			// Mit vorher ausgewähltem Speicherort speichern
			if (fileHandle) {
				console.log('[PDF Export] Writing to file...');
				const writable = await fileHandle.createWritable();
				await writable.write(pdfBlob);
				await writable.close();
				console.log('[PDF Export] File saved');
				await this.confirmService.alert('PDF wurde gespeichert.', 'Export erfolgreich');
			} else {
				console.log('[PDF Export] Using fallback download');
				// Fallback: klassischer Download
				this.fileExport.download({
					filename: filename,
					mimeType: 'application/pdf',
					content: pdfBlob
				});
				await this.confirmService.alert('PDF wurde heruntergeladen.', 'Export erfolgreich');
			}
		} catch (e) {
			console.error('PDF export failed:', e);
			alert('PDF-Export fehlgeschlagen. Bitte versuchen Sie es mit der Drucken-Funktion.');
		} finally {
			this.pdfExporting = false;
		}
	}
}
